import type { FormInstance } from "antd";
import { useCallback } from "react";
import { useI18n, type MessageKey } from "@/i18n";
import { ApiError } from "./client";

/** Message for an error: translated by code, with a reference id for 5xx. */
export function useErrorText() {
  const { t, tDynamic } = useI18n();
  return useCallback(
    (err: unknown): string => {
      if (!(err instanceof ApiError)) return t("error.generic");
      if (err.code === "network") return t("error.network");
      const text = tDynamic(`error.${err.code}`, "error.generic");
      if (err.status >= 500 && err.requestId) return `${text} ${t("common.reference", { id: err.requestId })}`;
      return text;
    },
    [t, tDynamic],
  );
}

/**
 * Puts server-side field errors onto an Ant Design form. Returns true when it
 * handled the error, so the caller only shows a toast for everything else.
 */
export function useApplyFieldErrors() {
  const { tDynamic } = useI18n();
  return useCallback(
    (form: FormInstance, err: unknown, fieldMap: Record<string, string> = {}): boolean => {
      if (!(err instanceof ApiError) || Object.keys(err.fields).length === 0) return false;
      form.setFields(
        Object.entries(err.fields).map(([name, code]) => ({
          name: fieldMap[name] ?? name,
          errors: [tDynamic(`field.${code}`, "field.invalid" satisfies MessageKey)],
        })),
      );
      return true;
    },
    [tDynamic],
  );
}
